import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';

const CATEGORIAS = ['Mobiliario', 'Equipos', 'Vehiculos', 'Edificios', 'Terrenos', 'Software', 'Otro'];
const METODOS    = [{ value: 'linea_recta', label: 'Linea Recta' }, { value: 'acelerada', label: 'Doble Saldo Decreciente' }];

const fmtRD = (n) => Number(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const periodoActual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const fechaHoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const MODAL_DEFAULT = {
  codigo: '', nombre: '', descripcion: '', categoria: 'Equipos',
  fecha_compra: fechaHoy(),
  costo_adquisicion: '', valor_residual: '0', vida_util_meses: '60',
  metodo_depreciacion: 'linea_recta',
  cuenta_activo_id: '', cuenta_depreciacion_id: '', cuenta_gasto_id: '',
};

export default function ActivosFijos() {
  const [tab, setTab]             = useState('activos');
  const [activos, setActivos]     = useState([]);
  const [resumen, setResumen]     = useState(null);
  const [preview, setPreview]     = useState(null);
  const [historial, setHistorial] = useState([]);
  const [cuentas, setCuentas]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [periodo, setPeriodo]     = useState(periodoActual());

  const [modalOpen, setModalOpen]   = useState(false);
  const [editando, setEditando]     = useState(null);
  const [form, setForm]             = useState(MODAL_DEFAULT);
  const [historialActivo, setHistorialActivo] = useState(null);

  const [filtro, setFiltro]         = useState('');
  const [soloCat, setSoloCat]       = useState('');

  const cargarActivos = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/activos-fijos');
      setActivos(r.data);
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || e.message, 'error');
    } finally { setLoading(false); }
  }, []);

  const cargarResumen = useCallback(async () => {
    try {
      const r = await api.get('/activos-fijos/resumen');
      setResumen(r.data);
    } catch {}
  }, []);

  const cargarCuentas = useCallback(async () => {
    try {
      const r = await api.get('/contabilidad/cuentas');
      const raw = Array.isArray(r.data) ? r.data : (r.data.cuentas || []);
      const aplanar = (items) => {
        const result = [];
        for (const c of items) {
          if (c.acepta_movimientos) result.push(c);
          if (c.hijos?.length) result.push(...aplanar(c.hijos));
        }
        return result;
      };
      setCuentas(aplanar(raw));
    } catch {}
  }, []);

  const cargarPreview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/activos-fijos/depreciacion/preview', { params: { periodo } });
      setPreview(r.data);
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || e.message, 'error');
    } finally { setLoading(false); }
  }, [periodo]);

  useEffect(() => {
    cargarActivos();
    cargarResumen();
    cargarCuentas();
  }, [cargarActivos, cargarResumen, cargarCuentas]);

  useEffect(() => {
    if (tab === 'depreciacion') cargarPreview();
  }, [tab, cargarPreview]);

  const abrirCrear = () => {
    setEditando(null);
    // Calcular próximo código AST-XXX a partir de los activos ya cargados
    const codigos = activos
      .map(a => a.codigo)
      .filter(c => c && c.startsWith('AST-'))
      .map(c => parseInt(c.replace('AST-', ''), 10))
      .filter(n => !isNaN(n));
    const base = codigos.length > 0 ? Math.max(...codigos) : activos.length;
    const proximoCodigo = `AST-${(base + 1).toString().padStart(3, '0')}`;
    setForm({ ...MODAL_DEFAULT, codigo: proximoCodigo });
    setModalOpen(true);
  };

  const abrirEditar = (a) => {
    setEditando(a.id);
    setForm({
      codigo: a.codigo || '',
      nombre: a.nombre || '',
      descripcion: a.descripcion || '',
      categoria: a.categoria || 'Equipos',
      fecha_compra: a.fecha_compra?.slice(0, 10) || '',
      costo_adquisicion: a.costo_adquisicion || '',
      valor_residual: a.valor_residual || '0',
      vida_util_meses: a.vida_util_meses || '60',
      metodo_depreciacion: a.metodo_depreciacion || 'linea_recta',
      cuenta_activo_id: a.cuenta_activo_id || '',
      cuenta_depreciacion_id: a.cuenta_depreciacion_id || '',
      cuenta_gasto_id: a.cuenta_gasto_id || '',
    });
    setModalOpen(true);
  };

  const guardar = async () => {
    if (!form.nombre || !form.costo_adquisicion) {
      Swal.fire('Validacion', 'Nombre y Costo son requeridos', 'warning');
      return;
    }
    const payload = {
      ...form,
      valor_residual:    parseFloat(form.valor_residual)    || 0,
      costo_adquisicion: parseFloat(form.costo_adquisicion) || 0,
      vida_util_meses:   parseInt(form.vida_util_meses)     || 60,
      cuenta_activo_id:       form.cuenta_activo_id       || null,
      cuenta_depreciacion_id: form.cuenta_depreciacion_id || null,
      cuenta_gasto_id:        form.cuenta_gasto_id        || null,
    };
    try {
      if (editando) {
        await api.put(`/activos-fijos/${editando}`, payload);
      } else {
        await api.post('/activos-fijos', payload);
      }
      setModalOpen(false);
      cargarActivos();
      cargarResumen();
      Swal.fire('Listo', editando ? 'Activo actualizado' : 'Activo creado', 'success');
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || e.message, 'error');
    }
  };

  const darDeBaja = async (a) => {
    const { isConfirmed } = await Swal.fire({
      title: 'Dar de baja',
      text: `Confirma dar de baja "${a.nombre}"?`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Si, dar de baja', cancelButtonText: 'Cancelar',
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/activos-fijos/${a.id}`);
      cargarActivos(); cargarResumen();
      Swal.fire('Listo', 'Activo dado de baja', 'success');
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || e.message, 'error');
    }
  };

  const verHistorial = async (a) => {
    try {
      const r = await api.get(`/activos-fijos/${a.id}/historial`);
      setHistorial(r.data);
      setHistorialActivo(a);
      setTab('historial');
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || e.message, 'error');
    }
  };

  const aplicarDepreciacion = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'Aplicar Depreciacion',
      html: `Se registrara la depreciacion del periodo <b>${periodo}</b> para todos los activos activos.<br><br>Esta accion no se puede deshacer.`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Aplicar', cancelButtonText: 'Cancelar',
    });
    if (!isConfirmed) return;
    setLoading(true);
    try {
      const r = await api.post('/activos-fijos/depreciacion/aplicar', { periodo });
      cargarActivos(); cargarResumen(); cargarPreview();
      Swal.fire('Listo', `Depreciacion aplicada: RD$ ${fmtRD(r.data.total_depreciacion)} para ${r.data.registros.length} activos`, 'success');
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || e.message, 'error');
    } finally { setLoading(false); }
  };

  const activosFiltrados = activos.filter(a => {
    const txt = filtro.toLowerCase();
    const matchTxt = !txt || a.nombre?.toLowerCase().includes(txt) || a.codigo?.toLowerCase().includes(txt);
    const matchCat = !soloCat || a.categoria === soloCat;
    return matchTxt && matchCat;
  });

  const sty = {
    page:    { padding: 24, fontFamily: 'sans-serif' },
    h1:      { margin: '0 0 16px', fontSize: 22, fontWeight: 700 },
    tabs:    { display: 'flex', gap: 8, marginBottom: 20 },
    tab:     (active) => ({ padding: '8px 18px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: active ? 700 : 400, background: active ? '#1e3a5f' : '#e2e8f0', color: active ? '#fff' : '#374151' }),
    kpi:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12, marginBottom: 20 },
    kpiCard: (color) => ({ background: color, borderRadius: 10, padding: '14px 18px', color: '#fff' }),
    kpiLbl:  { fontSize: 11, opacity: 0.85, marginBottom: 4 },
    kpiVal:  { fontSize: 20, fontWeight: 700 },
    card:    { background: '#fff', borderRadius: 10, boxShadow: '0 1px 6px rgba(0,0,0,.08)', padding: 20, marginBottom: 20 },
    btn:     (color) => ({ padding: '8px 16px', border: 'none', borderRadius: 6, cursor: 'pointer', background: color, color: '#fff', fontWeight: 600, fontSize: 13 }),
    tbl:     { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th:      { padding: '8px 10px', background: '#f1f5f9', fontWeight: 600, textAlign: 'left', borderBottom: '2px solid #e2e8f0' },
    td:      { padding: '7px 10px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
    badge:   (color) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: color + '22', color: color }),
    input:   { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
    select:  { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 },
    label:   { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#374151' },
    grid2:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    modal:   { background: '#fff', borderRadius: 12, padding: 28, width: 680, maxHeight: '90vh', overflowY: 'auto' },
  };

  const SelectCuenta = ({ label, field }) => (
    <div>
      <label style={sty.label}>{label}</label>
      <select style={sty.select} value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}>
        <option value="">-- Sin asignar --</option>
        {cuentas.map(c => <option key={c.id} value={c.id}>{c.codigo} - {c.nombre}</option>)}
      </select>
    </div>
  );

  return (
    <div style={sty.page}>
      <h1 style={sty.h1}>Activos Fijos y Depreciacion</h1>

      {/* KPI Cards */}
      {resumen && (
        <div style={sty.kpi}>
          <div style={sty.kpiCard('#1e3a5f')}>
            <div style={sty.kpiLbl}>Total Activos</div>
            <div style={sty.kpiVal}>{resumen.total_activos}</div>
          </div>
          <div style={sty.kpiCard('#0f766e')}>
            <div style={sty.kpiLbl}>Costo Total</div>
            <div style={sty.kpiVal}>RD$ {fmtRD(resumen.total_costo)}</div>
          </div>
          <div style={sty.kpiCard('#b45309')}>
            <div style={sty.kpiLbl}>Dep. Acumulada</div>
            <div style={sty.kpiVal}>RD$ {fmtRD(resumen.total_depreciacion_acumulada)}</div>
          </div>
          <div style={sty.kpiCard('#2563eb')}>
            <div style={sty.kpiLbl}>Valor en Libros</div>
            <div style={sty.kpiVal}>RD$ {fmtRD(resumen.total_valor_libros)}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={sty.tabs}>
        <button style={sty.tab(tab === 'activos')}      onClick={() => setTab('activos')}>Activos</button>
        <button style={sty.tab(tab === 'depreciacion')} onClick={() => setTab('depreciacion')}>Depreciacion Mensual</button>
        {historialActivo && (
          <button style={sty.tab(tab === 'historial')} onClick={() => setTab('historial')}>
            Historial: {historialActivo.nombre}
          </button>
        )}
      </div>

      {/* ── TAB ACTIVOS ── */}
      {tab === 'activos' && (
        <div style={sty.card}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              style={{ ...sty.input, width: 220 }}
              placeholder="Buscar activo..."
              value={filtro} onChange={e => setFiltro(e.target.value)}
            />
            <select style={{ ...sty.select, width: 160 }} value={soloCat} onChange={e => setSoloCat(e.target.value)}>
              <option value="">Todas categorias</option>
              {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button style={sty.btn('#1e3a5f')} onClick={abrirCrear}>+ Nuevo Activo</button>
          </div>

          {loading ? <p>Cargando...</p> : (
            <table style={sty.tbl}>
              <colgroup>
                <col style={{ width: '9%' }} /><col style={{ width: '22%' }} />
                <col style={{ width: '11%' }} /><col style={{ width: '11%' }} />
                <col style={{ width: '11%' }} /><col style={{ width: '11%' }} />
                <col style={{ width: '11%' }} /><col style={{ width: '14%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={sty.th}>Codigo</th>
                  <th style={sty.th}>Nombre</th>
                  <th style={sty.th}>Categoria</th>
                  <th style={{ ...sty.th, textAlign: 'right' }}>Costo</th>
                  <th style={{ ...sty.th, textAlign: 'right' }}>Dep. Acum.</th>
                  <th style={{ ...sty.th, textAlign: 'right' }}>Valor Libros</th>
                  <th style={sty.th}>Metodo</th>
                  <th style={sty.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {activosFiltrados.map(a => {
                  const vl = parseFloat(a.valor_libros) || 0;
                  const pct = a.costo_adquisicion > 0 ? Math.round((parseFloat(a.depreciacion_acumulada) / parseFloat(a.costo_adquisicion)) * 100) : 0;
                  return (
                    <tr key={a.id}>
                      <td style={sty.td}><code>{a.codigo}</code></td>
                      <td style={sty.td}>
                        <div style={{ fontWeight: 600 }}>{a.nombre}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{a.descripcion}</div>
                      </td>
                      <td style={sty.td}>
                        <span style={sty.badge('#6366f1')}>{a.categoria}</span>
                      </td>
                      <td style={{ ...sty.td, textAlign: 'right' }}>{fmtRD(a.costo_adquisicion)}</td>
                      <td style={{ ...sty.td, textAlign: 'right' }}>
                        {fmtRD(a.depreciacion_acumulada)}
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>{pct}% dep.</div>
                      </td>
                      <td style={{ ...sty.td, textAlign: 'right', fontWeight: 600, color: vl > 0 ? '#0f766e' : '#94a3b8' }}>
                        {fmtRD(vl)}
                      </td>
                      <td style={sty.td}>
                        <span style={sty.badge(a.metodo_depreciacion === 'linea_recta' ? '#2563eb' : '#7c3aed')}>
                          {a.metodo_depreciacion === 'linea_recta' ? 'Lineal' : 'Acelerada'}
                        </span>
                      </td>
                      <td style={sty.td}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button style={{ ...sty.btn('#2563eb'), padding: '4px 8px', fontSize: 11 }} onClick={() => abrirEditar(a)}>Editar</button>
                          <button style={{ ...sty.btn('#0f766e'), padding: '4px 8px', fontSize: 11 }} onClick={() => verHistorial(a)}>Historial</button>
                          {a.activo && (
                            <button style={{ ...sty.btn('#dc2626'), padding: '4px 8px', fontSize: 11 }} onClick={() => darDeBaja(a)}>Baja</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {activosFiltrados.length === 0 && (
                  <tr><td colSpan={8} style={{ ...sty.td, textAlign: 'center', color: '#94a3b8', padding: 30 }}>
                    No hay activos registrados
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── TAB DEPRECIACION ── */}
      {tab === 'depreciacion' && (
        <div style={sty.card}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20 }}>
            <div>
              <label style={sty.label}>Periodo (YYYY-MM)</label>
              <input style={{ ...sty.input, width: 140 }} type="month" value={periodo}
                onChange={e => setPeriodo(e.target.value)} />
            </div>
            <button style={{ ...sty.btn('#0f766e'), marginTop: 18 }} onClick={cargarPreview} disabled={loading}>
              Calcular Preview
            </button>
            {preview && (
              <button style={{ ...sty.btn('#1e3a5f'), marginTop: 18 }} onClick={aplicarDepreciacion} disabled={loading}>
                Aplicar Depreciacion
              </button>
            )}
          </div>

          {loading && <p>Calculando...</p>}

          {preview && !loading && (
            <>
              <div style={sty.kpi}>
                <div style={sty.kpiCard('#0f766e')}>
                  <div style={sty.kpiLbl}>Total a Depreciar</div>
                  <div style={sty.kpiVal}>RD$ {fmtRD(preview.total_depreciacion)}</div>
                </div>
                <div style={sty.kpiCard('#1e3a5f')}>
                  <div style={sty.kpiLbl}>Activos Pendientes</div>
                  <div style={sty.kpiVal}>{preview.activos.filter(a => a.motivo === 'Pendiente').length}</div>
                </div>
                <div style={sty.kpiCard('#64748b')}>
                  <div style={sty.kpiLbl}>Ya Aplicados</div>
                  <div style={sty.kpiVal}>{preview.activos.filter(a => a.motivo === 'Ya aplicado').length}</div>
                </div>
              </div>

              <table style={sty.tbl}>
                <colgroup>
                  <col style={{ width: '10%' }} /><col style={{ width: '24%' }} />
                  <col style={{ width: '11%' }} /><col style={{ width: '13%' }} />
                  <col style={{ width: '13%' }} /><col style={{ width: '13%' }} />
                  <col style={{ width: '16%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={sty.th}>Codigo</th>
                    <th style={sty.th}>Nombre</th>
                    <th style={sty.th}>Metodo</th>
                    <th style={{ ...sty.th, textAlign: 'right' }}>Valor Libros</th>
                    <th style={{ ...sty.th, textAlign: 'right' }}>Cuota</th>
                    <th style={{ ...sty.th, textAlign: 'right' }}>Valor Fin</th>
                    <th style={sty.th}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.activos.map(a => (
                    <tr key={a.id} style={{ opacity: a.motivo !== 'Pendiente' ? 0.55 : 1 }}>
                      <td style={sty.td}><code>{a.codigo}</code></td>
                      <td style={sty.td}>{a.nombre}</td>
                      <td style={sty.td}>{a.metodo === 'linea_recta' ? 'Lineal' : 'Acelerada'}</td>
                      <td style={{ ...sty.td, textAlign: 'right' }}>{fmtRD(a.valor_libros)}</td>
                      <td style={{ ...sty.td, textAlign: 'right', fontWeight: 600, color: a.cuota > 0 ? '#b45309' : '#94a3b8' }}>
                        {fmtRD(a.cuota)}
                      </td>
                      <td style={{ ...sty.td, textAlign: 'right' }}>{fmtRD(a.valor_libros_fin)}</td>
                      <td style={sty.td}>
                        <span style={sty.badge(
                          a.motivo === 'Pendiente' ? '#0f766e' :
                          a.motivo === 'Ya aplicado' ? '#2563eb' : '#64748b'
                        )}>{a.motivo}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* ── TAB HISTORIAL ── */}
      {tab === 'historial' && historialActivo && (
        <div style={sty.card}>
          <h3 style={{ margin: '0 0 14px', fontSize: 16 }}>Historial: {historialActivo.nombre} ({historialActivo.codigo})</h3>
          <table style={sty.tbl}>
            <colgroup>
              <col style={{ width: '12%' }} /><col style={{ width: '22%' }} />
              <col style={{ width: '22%' }} /><col style={{ width: '22%' }} />
              <col style={{ width: '22%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={sty.th}>Periodo</th>
                <th style={{ ...sty.th, textAlign: 'right' }}>Cuota</th>
                <th style={{ ...sty.th, textAlign: 'right' }}>Valor Inicio</th>
                <th style={{ ...sty.th, textAlign: 'right' }}>Valor Fin</th>
                <th style={sty.th}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {historial.length === 0 ? (
                <tr><td colSpan={5} style={{ ...sty.td, textAlign: 'center', color: '#94a3b8', padding: 20 }}>Sin registros</td></tr>
              ) : historial.map(h => (
                <tr key={h.id}>
                  <td style={sty.td}><b>{h.periodo}</b></td>
                  <td style={{ ...sty.td, textAlign: 'right', color: '#b45309', fontWeight: 600 }}>{fmtRD(h.monto)}</td>
                  <td style={{ ...sty.td, textAlign: 'right' }}>{fmtRD(h.valor_libros_inicio)}</td>
                  <td style={{ ...sty.td, textAlign: 'right' }}>{fmtRD(h.valor_libros_fin)}</td>
                  <td style={sty.td}>
                    <span style={sty.badge(h.aplicada ? '#0f766e' : '#64748b')}>{h.aplicada ? 'Aplicada' : 'Borrador'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── MODAL CREAR/EDITAR ── */}
      {modalOpen && (
        <div style={sty.overlay} onClick={e => { if (e.target === e.currentTarget) setModalOpen(false); }}>
          <div style={sty.modal}>
            <h3 style={{ margin: '0 0 18px', fontSize: 18 }}>{editando ? 'Editar Activo' : 'Nuevo Activo Fijo'}</h3>

            <div style={{ ...sty.grid2, marginBottom: 12 }}>
              <div>
                <label style={sty.label}>Codigo <span style={{ fontWeight: 400, color: '#94a3b8' }}>(editable)</span></label>
                <input style={{ ...sty.input, background: editando ? '#fff' : '#f0f9ff', fontWeight: 600 }} value={form.codigo} onChange={e => setForm(f => ({ ...f, codigo: e.target.value }))} />
              </div>
              <div>
                <label style={sty.label}>Nombre *</label>
                <input style={sty.input} value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={sty.label}>Descripcion</label>
              <input style={sty.input} value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} />
            </div>

            <div style={{ ...sty.grid2, marginBottom: 12 }}>
              <div>
                <label style={sty.label}>Categoria</label>
                <select style={sty.select} value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}>
                  {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={sty.label}>Fecha Compra</label>
                <input style={sty.input} type="date" value={form.fecha_compra} onChange={e => setForm(f => ({ ...f, fecha_compra: e.target.value }))} />
              </div>
            </div>

            <div style={{ ...sty.grid2, marginBottom: 12 }}>
              <div>
                <label style={sty.label}>Costo Adquisicion (RD$) *</label>
                <input style={sty.input} type="number" min="0" step="0.01" value={form.costo_adquisicion} onChange={e => setForm(f => ({ ...f, costo_adquisicion: e.target.value }))} />
              </div>
              <div>
                <label style={sty.label}>Valor Residual (RD$)</label>
                <input style={sty.input} type="number" min="0" step="0.01" value={form.valor_residual} onChange={e => setForm(f => ({ ...f, valor_residual: e.target.value }))} />
              </div>
            </div>

            <div style={{ ...sty.grid2, marginBottom: 12 }}>
              <div>
                <label style={sty.label}>Vida Util (meses)</label>
                <input style={sty.input} type="number" min="1" value={form.vida_util_meses} onChange={e => setForm(f => ({ ...f, vida_util_meses: e.target.value }))} />
              </div>
              <div>
                <label style={sty.label}>Metodo Depreciacion</label>
                <select style={sty.select} value={form.metodo_depreciacion} onChange={e => setForm(f => ({ ...f, metodo_depreciacion: e.target.value }))}>
                  {METODOS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ background: '#f8fafc', borderRadius: 8, padding: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 10 }}>Cuentas Contables</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                <SelectCuenta label="Cuenta Activo (ej: 1.2.1 Mobiliario)" field="cuenta_activo_id" />
                <SelectCuenta label="Cuenta Dep. Acumulada (ej: 1.2.2)" field="cuenta_depreciacion_id" />
                <SelectCuenta label="Cuenta Gasto Depreciacion (ej: 6.5)" field="cuenta_gasto_id" />
              </div>
            </div>

            {/* Preview cuota */}
            {form.costo_adquisicion && form.vida_util_meses && (
              <div style={{ background: '#eff6ff', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>
                <b>Cuota mensual estimada:</b>{' '}
                RD$ {fmtRD(
                  form.metodo_depreciacion === 'linea_recta'
                    ? (parseFloat(form.costo_adquisicion) - parseFloat(form.valor_residual || 0)) / parseInt(form.vida_util_meses)
                    : (2 / parseInt(form.vida_util_meses)) * parseFloat(form.costo_adquisicion)
                )}
                {form.metodo_depreciacion === 'acelerada' && <span style={{ color: '#7c3aed', marginLeft: 8 }}>(primer periodo, decrece)</span>}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button style={sty.btn('#64748b')} onClick={() => setModalOpen(false)}>Cancelar</button>
              <button style={sty.btn('#1e3a5f')} onClick={guardar}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
